// src/common/ui/citation-results.js
// Citation result rendering utilities

import { elTable } from './dom-utils.js';
import { badgeClass, mapResultToStatus, iconHTML } from './badges-status.js';
import { normalizeAuditEntries } from './data-normalizers.js';
import { formatCitationType } from '../utils/type-formatter.js';
import '../joint-results.css';

/**
 * Update header status icons for a footnote card
 * @param {HTMLElement} appEl - App container element
 * @param {string|number} footnoteId - Footnote ID
 * @param {Object} resultObj - Results object
 * @param {Object} jointResults - Final results keyed by joint group ID
 */
export function updateHeaderStatus(appEl, footnoteId, resultObj, jointResults = {}) {
  const card = appEl.querySelector(`.card[data-id="${CSS.escape(String(footnoteId))}"]`);
  if (!card) return;
  const header = card.querySelector('.card-header');
  const bar = header.querySelector('.status-icons');
  if (!bar) return;

  const entries = Object.entries(resultObj || {});
  entries.sort((a, b) => {
    const nx = parseInt(String(a[0]).split('-')[1] || '0', 10);
    const ny = parseInt(String(b[0]).split('-')[1] || '0', 10);
    return nx - ny;
  });

  const html = entries.map(([cid, data]) => {
    const kind = mapResultToStatus(data);
    const contribution = data?.joint_member_result?.contribution_label
      || data?.congruence?.joint_member_result?.contribution_label
      || data?.congruence_result?.joint_member_result?.contribution_label;
    const label = contribution
      ? `${contribution.replaceAll('_', ' ')} (joint check)`
      : data?.label || data?.analyze_result?.label || data?.analyze_result?.congruence_result?.label || data?.status || 'unresolved';
    const title = `Citation ${cid}: ${label}`;
    return iconHTML(kind, title);
  }).join('');

  bar.innerHTML = html;

  // Member marks describe individual contributions. Show the combined verdict
  // separately so five green marks cannot read as full support for a partial group.
  const groups = new Map(Object.entries(jointResults || {}));
  for (const [, data] of entries) {
    const group = data?.joint_group_result || data?.congruence?.joint_group_result
      || data?.congruence_result?.joint_group_result;
    const groupId = data?.joint_evaluation_group || group?.group_id;
    if (groupId && (!groups.has(groupId) || (!groups.get(groupId) && group))) {
      groups.set(groupId, group || null);
    }
  }
  for (const [groupId, group] of groups) {
    const label = String(group?.label || 'pending').toLowerCase();
    const state = label === 'supports' ? 'supports'
      : ['partial', 'insufficient'].includes(label) ? 'partial'
      : ['pending', 'error'].includes(label) ? 'pending' : 'issue';
    const indicator = document.createElement('span');
    indicator.className = `joint-header-indicator joint-header-indicator--${state}`;
    indicator.textContent = `Joint · ${label === 'supports' ? 'verified' : label === 'insufficient' ? 'incomplete' : label}`;
    indicator.title = label === 'partial'
      ? 'Joint evaluation: individual checks completed, but the combined evidence only partially supports the claim.'
      : label === 'supports'
        ? 'Joint evaluation: the combined evidence supports the claim.'
        : label === 'pending'
          ? 'Joint evaluation is pending.'
          : `Joint evaluation ${groupId}: ${label.replaceAll('_', ' ')}.`;
    bar.append(indicator);
  }
}

/**
 * Render initial citation placeholders with parsed data
 * @param {HTMLElement} resultsContainer - Results container element
 * @param {Array} citations - Array of citation objects
 * @param {Object} options - Rendering options
 * @param {string|null} options.statusMessage - Optional status message; defaults to Parsing...
 */
export function renderInitialCitationPlaceholders(resultsContainer, citations, options = {}) {
  const { statusMessage = 'Parsing...' } = options;
  resultsContainer.innerHTML = ''; // Clear any previous results or asks
  
  // Ensure file catalog is loaded to get source_url for cached citations (async, but don't block rendering)
  const mgr = window.footcheckFootnotesManager;
  if (mgr && mgr.fileCatalog && mgr.fileCatalog.size === 0) {
    // Load in background - will be available on next render if needed
    mgr.ensureFileCatalog().catch(err => {
      console.warn('[renderInitialCitationPlaceholders] Failed to load file catalog:', err);
    });
  }
  
  citations.forEach(citation => {
    const block = document.createElement('div');
    block.className = 'citation-block';
    block.dataset.citeId = citation.id;
    
    // Create top row with title and status
    const topRow = document.createElement('div');
    topRow.className = 'row';
    
    const titleDiv = document.createElement('div');
    titleDiv.innerHTML = `<strong>Citation ${citation.id}</strong>`;
    
    const spacer = document.createElement('div');
    spacer.className = 'spacer';
    
    // Create status element for citation-level progress indicators
    const statusEl = document.createElement('div');
    statusEl.className = 'citation-status';
    if (statusMessage) {
      statusEl.innerHTML = `<span class="pending">${statusMessage}</span>`;
    }
    
    topRow.append(titleDiv, spacer, statusEl);
    
    // Create simplified table with parsed data (raw, type, url/doi, context)
    const rows = [
      ['Citation', citation.source?.raw ?? '—'],
      ['Type', formatCitationType(citation.source?.type)],
    ];
    
    // Check for source URL from citation or from resolved file
    let sourceUrl = citation.source?.url_doi;
    
    // If citation was resolved and has a file_id, try to get source_url from file catalog
    const citationSourceDb = citation.source_db || citation.resolve_result?.source_db || 'doc';
    if (!sourceUrl && citation.file_id && citationSourceDb === 'doc') {
      const mgr = window.footcheckFootnotesManager;
      if (mgr?.getFileSourceUrl) {
        sourceUrl = mgr.getFileSourceUrl(citation.file_id, citationSourceDb);
      } else if (mgr?.fileCatalog) {
        const fileInfo = mgr.fileCatalog.get(String(citation.file_id));
        sourceUrl = fileInfo?.source_url || null;
      }
    }
    
    if (sourceUrl) {
      const link = document.createElement('a');
      link.href = sourceUrl;
      link.target = '_blank';
      link.textContent = 'Link';
      link.className = 'source-link';
      link.title = sourceUrl; // Full URL in tooltip
      
      // Add click handler for Tauri mode (only if not already attached)
      if (window.__TAURI__ && !link.dataset.handlerAttached) {
        link.dataset.handlerAttached = 'true';
        link.addEventListener('click', async (e) => {
          e.preventDefault();
          e.stopPropagation(); // Prevent event bubbling
          try {
            const { shell } = window.__TAURI__;
            await shell.open(sourceUrl);
          } catch (error) {
            console.error('Failed to open URL:', error);
            window.open(sourceUrl, '_blank');
          }
        }, { once: false, passive: false });
      }
      
      rows.push(['Source URL', link]);
    } else {
      rows.push(['Source URL', '—']);
    }
    
    // Claim (LLM-extracted) preferred; raw context only as a fallback
    const claim = citation.claim;
    if (claim && String(claim).trim()) {
      rows.push(['Claim', claim]);
    } else {
      rows.push(['Context', citation.context ?? '—']);
    }
    
    const table = elTable(rows);
    
    block.append(topRow, table);
    resultsContainer.appendChild(block);
  });
}

/**
 * Update citation UI status
 * @param {HTMLElement} resultsContainer - Results container element
 * @param {string} citationId - Citation ID
 * @param {Object} status - Status object with state and message
 */
export function updateCitationUI(resultsContainer, citationId, { state, message }) {
  // If citationId is null, update all citations (used for parsing stage)
  if (citationId === null) {
    const blocks = resultsContainer.querySelectorAll('.citation-block');
    blocks.forEach(block => {
      const statusEl = block.querySelector('.citation-status');
      if (statusEl) {
        if (state === 'parsing') {
          statusEl.innerHTML = '<span class="pending">Parsing...</span>';
        } else {
          statusEl.innerHTML = `<span class="pending">${message || 'Processing...'}</span>`;
        }
      }
    });
    return;
  }

  const block = resultsContainer.querySelector(`.citation-block[data-cite-id="${citationId}"]`);
  if (!block) return;

  const statusEl = block.querySelector('.citation-status');
  if (statusEl) {
    if (state === 'parsing') {
      statusEl.innerHTML = '<span class="pending">Parsing...</span>';
    } else if (state === 'resolving') {
      statusEl.innerHTML = '<span class="pending">Resolving...</span>';
    } else if (state === 'analyzing') {
      statusEl.innerHTML = '<span class="pending">Analyzing...</span>';
    } else if (state === 'resolved') {
      statusEl.innerHTML = `<span class="pending pending-resolved">${message || 'Resolved'}</span>`;
    } else if (state === 'filtered') {
      statusEl.innerHTML = `<span class="pending pending-filtered">${message || 'Excluded'}</span>`;
    } else if (state === 'error') {
      statusEl.innerHTML = `<span class="pending pending-error">${message || 'Error'}</span>`;
    } else if (state === 'complete') {
      // Clear status when complete (final results will be rendered)
      statusEl.innerHTML = '';
    } else {
      statusEl.innerHTML = `<span class="pending">${message || 'Processing...'}</span>`;
    }
  }
}

/**
 * Render a single citation block
 * @param {HTMLElement} block - Block element
 * @param {string} cid - Citation ID
 * @param {Object} data - Citation data
 * @param {Object} options - Rendering options
 */
function renderCitationBlock(block, cid, data, options = {}) {
  const webExists = data?.web_exists ?? data?.resolve_result?.web_exists;
  const paywalled = data?.paywalled ?? data?.resolve_result?.paywalled;
  const blockedFlag = data?.blocked ?? data?.resolve_result?.blocked;
  const methodVal = data?.method ?? data?.resolve_result?.method;

  console.log(`[DEBUG] Citation ${cid} web_exists:`, webExists);
  console.log(`[DEBUG] Citation ${cid} paywalled:`, paywalled);
  console.log(`[DEBUG] Citation ${cid} blocked:`, blockedFlag);

  // Clear the block and rebuild it
  block.innerHTML = '';
  block.style.marginTop = '10px';

  // Create top row with badges
  const topRow = createCitationTopRow(cid, data, webExists, paywalled, blockedFlag, methodVal, options);
  block.append(topRow);
  if (data?.joint_member_result) {
    block.dataset.jointGroup = data.joint_evaluation_group || '';
    const details = document.createElement('details');
    details.className = 'joint-contribution-details';
    const summary = document.createElement('summary');
    summary.textContent = 'Contribution evidence';
    const rationale = document.createElement('p');
    rationale.textContent = data.joint_member_result.rationale || '';
    details.append(summary, rationale);
    const coverage = document.createElement('p');
    const indices = data.joint_member_result.supported_claim_indices || [];
    coverage.textContent = indices.length
      ? `Contributes to claim ${indices.map(index => index + 1).join(', ')}`
      : 'No atomic claim coverage verified';
    details.append(coverage);
    for (const span of data.joint_member_result.evidence || []) {
      const evidence = document.createElement('blockquote');
      evidence.textContent = span.text || '';
      details.append(evidence);
    }
    block.append(details);
  }

  // Create data table
  const table = createCitationTable(cid, data, webExists, paywalled, blockedFlag, methodVal, options);
  block.append(table);

  // Create snippets section if available
  const snippetsSection = createSnippetsSection(data, cid);
  if (snippetsSection) {
    block.append(snippetsSection);
  }

  console.log(`[DEBUG] Rendered citation block for ${cid}`);
}

function clearFinalResultPendingMarkers(container) {
  if (!container) return;

  container.querySelectorAll('.citation-status').forEach(statusEl => {
    statusEl.remove();
  });

  container.querySelectorAll('.pending').forEach(pendingEl => {
    if (/^(analyzing|resolving|processing|parsing)/i.test((pendingEl.textContent || '').trim())) {
      pendingEl.remove();
    }
  });
}

/**
 * Create citation top row with title and badges
 * @param {string} cid - Citation ID
 * @param {Object} data - Citation data
 * @param {boolean} webExists - Web exists flag
 * @param {boolean} paywalled - Paywalled flag
 * @param {boolean} blockedFlag - Blocked flag
 * @param {string} methodVal - Method value
 * @returns {HTMLDivElement}
 */
function createCitationTopRow(cid, data, webExists, paywalled, blockedFlag, methodVal, options = {}) {
  const topRow = document.createElement('div');
  topRow.className = 'row citation-result-header';

  const title = document.createElement('div');
  const titleLabel = String(cid).startsWith('__') ? (data?.label || 'Notice') : `Citation ${cid}`;
  title.innerHTML = `<strong>${titleLabel}</strong>`;

  // Main status badge — verdict only; confidence lives in the compact Details row.
  const badge = document.createElement('span');
  const badgeLabel = data?.joint_member_result
    ? ({ contributes: 'Contribution verified', does_not_contribute: 'No contribution found',
         contradicts: 'Contradicts claim', 'non-verifiable': 'Contribution unverified' }
        [data.joint_member_result.contribution_label] || 'Joint contribution pending')
    : data?.label ?? data?.analysis?.label ?? data?.resolve_result?.label ?? data?.analyze_result?.congruence_result?.label ?? '—';
  badge.className = data?.joint_member_result
    ? `badge joint-member-badge ${data.joint_member_result.contribution_label === 'contributes' ? 'supports'
      : data.joint_member_result.contribution_label === 'non-verifiable' ? 'warn' : 'disputes'}`
    : badgeClass(badgeLabel);
  const badgeText = String(badgeLabel || '—').toLowerCase();
  // Confidence belongs in the collapsible Details row; keeping it out of the
  // badge saves header space and avoids presenting confidence as congruence.
  badge.textContent = badgeText;

  // Secondary labels (e.g., quote-mismatch when main label is 'supports')
  // These are flags from the LLM that don't change the main verdict but should be displayed
  const secondaryLabels = data?.secondary_labels ?? 
                          data?.congruence_result?.secondary_labels ?? 
                          data?.analyze_result?.congruence_result?.secondary_labels ?? 
                          [];
  const secondaryBadges = createSecondaryLabelBadges(secondaryLabels);

  // Add web status pills
  const webPills = createWebStatusPills(webExists, paywalled, blockedFlag, methodVal);
  
  const spacer = document.createElement('div'); 
  spacer.className = 'spacer';
  
  // Add all badges to the top row
  topRow.append(title, spacer, badge);
  if (data?.joint_member_result) {
    const jointPill = document.createElement('span');
    jointPill.className = 'badge joint-evaluation-pill';
    jointPill.textContent = 'Checked jointly';
    topRow.append(jointPill);
  }
  secondaryBadges.forEach(b => topRow.append(b));
  webPills.forEach(pill => topRow.append(pill));
  
  return topRow;
}

/**
 * Create badges for secondary labels (e.g., quote issues flagged by LLM)
 * @param {Array<string>} labels - Array of secondary label strings
 * @returns {Array<HTMLSpanElement>}
 */
function createSecondaryLabelBadges(labels) {
  if (!Array.isArray(labels) || labels.length === 0) return [];
  
  return labels.map(label => {
    const badge = document.createElement('span');
    badge.className = badgeClass(label) + ' secondary-badge';
    badge.textContent = String(label).toLowerCase().replace(/_/g, '-');
    badge.title = `Secondary issue: ${label}`;
    return badge;
  });
}

/**
 * Create web status pills (badges for web-related statuses)
 * @param {boolean} webExists - Web exists flag
 * @param {boolean} paywalled - Paywalled flag
 * @param {boolean} blockedFlag - Blocked flag
 * @param {string} methodVal - Method value
 * @returns {Array<HTMLSpanElement>}
 */
function createWebStatusPills(webExists, paywalled, blockedFlag, methodVal) {
  const webPills = [];

  if (webExists === true || webExists === false) {
    const webStatus = webExists ? 'exists' : 'unfound';
    const webBadge = document.createElement('span');
    webBadge.className = badgeClass(webStatus);

    if (webExists) {
      webBadge.innerHTML = '🌐 exists';
    } else {
      webBadge.textContent = 'unfound';
    }

    webPills.push(webBadge);
  }

  if (paywalled === true) {
    const paywalledBadge = document.createElement('span');
    paywalledBadge.className = badgeClass('paywalled');
    paywalledBadge.textContent = 'paywalled';
    webPills.push(paywalledBadge);
  }

  // Check if method contains "block" to detect blocked status
  if ((typeof methodVal === 'string' && methodVal.toLowerCase().includes('block')) || blockedFlag) {
    const blockedBadge = document.createElement('span');
    blockedBadge.className = badgeClass('blocked');
    blockedBadge.textContent = 'blocked';
    webPills.push(blockedBadge);
  }

  return webPills;
}

/**
 * Create citation data table
 * @param {string} cid - Citation ID
 * @param {Object} data - Citation data
 * @param {boolean} webExists - Web exists flag
 * @param {boolean} paywalled - Paywalled flag
 * @param {boolean} blockedFlag - Blocked flag
 * @param {string} methodVal - Method value
 * @param {Object} options - Rendering options
 * @returns {HTMLTableElement}
 */
function createCitationTable(cid, data, webExists, paywalled, blockedFlag, methodVal, options) {
  const rows = [];
  
  // Check if this is a parsed-only citation (not yet resolved/analyzed)
  const hasResultData = !!(
    data?.label ||
    data?.congruence_result ||
    data?.analysis ||
    data?.analyze_result ||
    data?.resolve ||
    data?.resolve_result ||
    methodVal
  );
  const isParsedOnly = !hasResultData && !data?.file_id && !data?.resolve_result?.file_id;
  let failureRowShown = false;

  if (isParsedOnly) {
    // Simplified view for parsed citations (inline or regular footnotes after parsing)
    // Show: raw, type, url/doi, context
    
    const raw = data?.source?.raw ?? data?.cite ?? data?.citation?.source?.raw ?? '—';
    const type = formatCitationType(data?.source?.type ?? data?.type);
    
    // Check for source URL - prioritize data.url (from verification results) if present and non-empty
    let sourceUrl = null;
    
    // First priority: check data.url (from verification results) - always include if present and non-empty
    if (data?.url && typeof data.url === 'string' && data.url.trim().length > 0) {
      sourceUrl = data.url.trim();
    } else {
      // Fallback to other sources
      sourceUrl = data?.source?.url_doi ?? data?.url_doi ?? null;
    }
    
    // If citation was resolved and has a file_id, try to get source_url from file catalog
    const dataSourceDb = (data?.source_db || data?.resolve_result?.source_db || 'doc');
    if (!sourceUrl && (data?.file_id || data?.file) && dataSourceDb === 'doc') {
      const fileId = data?.file_id ?? data?.file;
      if (options?.getFileSourceUrl) {
        sourceUrl = options.getFileSourceUrl(fileId, dataSourceDb);
      } else {
        const mgr = window.footcheckFootnotesManager;
        if (mgr?.getFileSourceUrl) {
          sourceUrl = mgr.getFileSourceUrl(fileId, dataSourceDb);
        } else if (mgr?.fileCatalog) {
          const fileInfo = mgr.fileCatalog.get(String(fileId));
          sourceUrl = fileInfo?.source_url || null;
        }
      }
    }
    
    rows.push(['Citation', raw]);
    rows.push(['Type', type]);
    
    // Always show Source URL row - show link if available, otherwise show '—'
    if (sourceUrl && typeof sourceUrl === 'string' && sourceUrl.trim().length > 0) {
      const link = document.createElement('a');
      link.href = sourceUrl;
      link.target = '_blank';
      link.textContent = 'Link';
      link.className = 'source-link';
      link.title = sourceUrl; // Full URL in tooltip
      
      // Add click handler for Tauri mode (only if not already attached)
      if (window.__TAURI__ && !link.dataset.handlerAttached) {
        link.dataset.handlerAttached = 'true';
        link.addEventListener('click', async (e) => {
          e.preventDefault();
          e.stopPropagation(); // Prevent event bubbling
          try {
            const { shell } = window.__TAURI__;
            await shell.open(sourceUrl);
          } catch (error) {
            console.error('Failed to open URL:', error);
            window.open(sourceUrl, '_blank');
          }
        }, { once: false, passive: false });
      }
      
      rows.push(['Source URL', link]);
    } else {
      rows.push(['Source URL', '—']);
    }
    
    const claim = data?.claim ?? data?.citation?.claim;
    if (claim && String(claim).trim()) {
      rows.push(['Claim', claim]);
    } else {
      rows.push(['Context', data?.context ?? data?.source?.context ?? '—']);
    }
  } else {
    // Full view for resolved/analyzed citations

    // --- File row with optional change button ---
    const fileDisplay = options.resolveFileDisplay ? options.resolveFileDisplay(data, cid) : null;
    const fileCell = document.createElement('div');
    fileCell.className = 'file-cell';
    const fileLabel = document.createElement('span');
    fileLabel.className = 'file-label';
    fileLabel.textContent = fileDisplay?.label ?? String(data?.file ?? data?.file_id ?? '—');
    fileCell.append(fileLabel);

    if (typeof options.onChangeFile === 'function' && fileDisplay?.fileId != null) {
      const changeBtn = document.createElement('button');
      changeBtn.type = 'button';
      changeBtn.className = 'file-change-btn';
      changeBtn.textContent = 'Choose file';
      changeBtn.style.marginLeft = '8px';
      changeBtn.addEventListener('click', (ev) => {
        ev.stopPropagation();
        options.onChangeFile(String(cid));
      });
      fileCell.append(changeBtn);
    }

    if (!String(cid).startsWith('__') && typeof options.onResetCitation === 'function') {
      const resetBtn = document.createElement('button');
      resetBtn.type = 'button';
      resetBtn.className = 'citation-reset-btn';
      resetBtn.setAttribute('aria-label', 'Clear citation result');
      resetBtn.title = 'Clear the current resolution and congruence';
      resetBtn.innerHTML = `
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path d="M3 6h18"></path>
          <path d="M8 6V4h8v2"></path>
          <path d="M19 6l-1 14H6L5 6"></path>
          <path d="M10 11v5"></path>
          <path d="M14 11v5"></path>
        </svg>
      `;
      resetBtn.addEventListener('click', (ev) => {
        ev.stopPropagation();
        options.onResetCitation(String(cid), { button: resetBtn });
      });
      fileCell.append(resetBtn);
    }

    rows.push(['File', fileCell]);

    // --- Cite: only show if different from the card header (Full text) ---
    const citeVal = data?.cite ?? data?.citation?.cite ?? data?.source?.raw ?? '';
    // Show cite only if it adds info beyond what's already visible in the header
    if (citeVal && citeVal !== '—') {
      rows.push(['Cite', citeVal]);
    }

    // --- Quote: inline badge + score in one row, skip if empty ---
    const q = data?.quote_check || data?.quoteCheck || null;
    if (q && q.label) {
      const qLabelMap = { exact: 'quote-exact', close: 'quote-close', stitched: 'quote-stitched', mismatch: 'quote-mismatch', 'not_found': 'quote-not-found', unverified: 'quote-unverified' };
      const mappedLabel = qLabelMap[q.label] || 'quote-not-found';
      const quoteCell = document.createElement('span');
      const qBadge = document.createElement('span');
      qBadge.className = badgeClass(mappedLabel);
      qBadge.textContent = mappedLabel;
      if (q.rationale) qBadge.title = q.rationale;  // surfaces omitted text for stitched quotes
      quoteCell.append(qBadge);
      if (typeof q.score === 'number') {
        const scoreSpan = document.createElement('span');
        scoreSpan.className = 'quote-score-inline';
        scoreSpan.textContent = ` ${Number(q.score).toFixed(2)}`;
        quoteCell.append(scoreSpan);
      }
      rows.push(['Quote', quoteCell]);
    }

    // --- Doc: only show if present ---
    const docVal = data?.doc ?? data?.document;
    if (docVal && docVal !== '—') {
      rows.push(['Doc', docVal]);
    }

    // --- Rationale: show the full resolver explanation ---
    const rationaleText = data?.rationale || data?.analysis?.rationale || data?.analyze_result?.congruence_result?.rationale || '';
    if (rationaleText && rationaleText !== '—') {
      const rationaleCell = document.createElement('div');
      rationaleCell.className = 'rationale-cell expanded';
      const textSpan = document.createElement('span');
      textSpan.className = 'rationale-text';
      textSpan.textContent = rationaleText;
      rationaleCell.append(textSpan);
      rows.push(['Rationale', rationaleCell]);
    }

    // --- Source URL: only show if present ---
    let sourceUrl = null;
    if (data?.url && typeof data.url === 'string' && data.url.trim().length > 0) {
      sourceUrl = data.url.trim();
    } else {
      sourceUrl = data?.source_url ?? data?.resolve_result?.url;
    }
    if (!sourceUrl && options?.resolveFileDisplay) {
      const fd = options.resolveFileDisplay(data, cid);
      if (fd?.info?.source_url) sourceUrl = fd.info.source_url;
    }
    if (!sourceUrl && options?.getFileSourceUrl) {
      const fileId = data?.file_id ?? data?.file ?? data?.resolve_result?.file_id;
      if (fileId) sourceUrl = options.getFileSourceUrl(fileId, (data?.source_db || data?.resolve_result?.source_db || 'doc'));
    }
    if (sourceUrl && typeof sourceUrl === 'string' && sourceUrl.trim().length > 0) {
      const link = createSourceLink(sourceUrl);
      rows.push(['Source URL', link]);
    }

    // --- Access: only show if paywalled ---
    if (paywalled === true) {
      const paywalledBadge = document.createElement('span');
      paywalledBadge.className = badgeClass('paywalled');
      paywalledBadge.textContent = 'paywalled';
      rows.push(['Access', paywalledBadge]);
    }

    // --- Failure reason: only show if resolution actually failed ---
    // A resolved file means the citation was verified against real text; any
    // failed_urls left over from earlier candidates are just a trail, not a failure.
    const resolvedFileId = fileDisplay?.fileId ?? data?.file ?? data?.file_id ?? data?.resolve_result?.file_id;
    if (resolvedFileId == null) {
      const failureReason = data?.failure_reason ?? data?.analysis?.failure_reason ?? data?.resolve_result?.failure_reason;
      const failedUrls = data?.failed_urls ?? data?.resolve_result?.failed_urls;
      if (failedUrls && Array.isArray(failedUrls) && failedUrls.length > 0) {
        const failureContainer = createFailureContainer(failedUrls);
        rows.push(['Failure Reason', failureContainer]);
        failureRowShown = true;
      } else if (failureReason) {
        rows.push(['Failure Reason', createFailureReasonNode(failureReason)]);
        failureRowShown = true;
      }
    }

    // --- Atomic claims: distinct propositions this citation supports ---
    const congruenceResult = data?.congruence_result ?? data?.analyze_result?.congruence_result ?? null;
    const atomicClaims = data?.atomic_claims
      ?? data?.citation?.atomic_claims
      ?? data?.analysis?.citation?.atomic_claims
      ?? null;
    const atomicVerdicts = congruenceResult?.atomic_claim_verdicts
      ?? data?.atomic_claim_verdicts
      ?? null;
    const citationFunction = congruenceResult?.citation_function
      ?? data?.citation?.citation_function
      ?? data?.citation_function
      ?? null;
    const citationFunctionConfidence = data?.citation?.citation_function_confidence
      ?? data?.citation_function_confidence
      ?? null;
    const citationFunctionRationale = data?.citation?.citation_function_rationale
      ?? data?.citation_function_rationale
      ?? null;
    const atomicCount = (Array.isArray(atomicVerdicts) && atomicVerdicts.length)
      || (Array.isArray(atomicClaims) && atomicClaims.length)
      || 0;
    // Surface the distinct-claims breakdown whenever at least one is present.
    const atomicClaimsList = atomicCount >= 1
      ? createAtomicClaimsList(atomicClaims, atomicVerdicts)
      : null;
    if (atomicClaimsList) {
      rows.push(['Claims', String(atomicCount)]);
    }

    // --- Collapsible details row (Method, Type, Confidence) ---
    const citationType = data?.citation?.source?.type ?? data?.source?.type;
    const calibration = data?.calibration ?? data?.analysis?.calibration ?? data?.analyze_result?.congruence_result?.calibration;
    const detailPairs = [
      ['Method', methodVal],
      ['Type', formatCitationType(citationType)],
      ['Function', typeof citationFunction === 'string' ? citationFunction.replace(/_/g, ' ') : null],
      ['Function confidence', citationFunctionConfidence != null ? String(citationFunctionConfidence) : null],
      ['Function rationale', citationFunctionRationale],
      ['Confidence', calibration != null ? String(calibration) : null],
    ].filter(([, v]) => v !== null && v !== undefined && v !== '' && v !== '—');

    if (detailPairs.length > 0 || atomicClaimsList) {
      const detailsRow = createDetailsToggle(detailPairs, [atomicClaimsList]);
      rows.push(['__details', detailsRow]);
    }
  }

  const table = elTable(rows.filter(([k]) => k !== '__details'));

  // Append details toggle after the table (not inside it)
  const detailsEntry = rows.find(([k]) => k === '__details');
  const wrapper = document.createElement('div');
  wrapper.append(table);
  if (detailsEntry) {
    wrapper.append(detailsEntry[1]);
  }

  // Apply special styling to failure reason row if present (only in full view)
  if (!isParsedOnly && failureRowShown) {
    const failureRow = table.querySelector('tr:last-child');
    if (failureRow) {
      failureRow.classList.add('failure-reason-row');
    }
  }

  return wrapper;
}

/**
 * Create a source URL link element with Tauri support
 * @param {string} sourceUrl - URL to link to
 * @returns {HTMLAnchorElement}
 */
function createSourceLink(sourceUrl) {
  sourceUrl = normalizeUrlCandidate(sourceUrl);

  const link = document.createElement('a');
  link.href = sourceUrl;
  link.target = '_blank';
  link.textContent = 'Link';
  link.className = 'source-link';
  link.title = sourceUrl;

  if (window.__TAURI__ && !link.dataset.handlerAttached) {
    link.dataset.handlerAttached = 'true';
    link.addEventListener('click', async (e) => {
      e.preventDefault();
      e.stopPropagation();
      try {
        const { shell } = window.__TAURI__;
        await shell.open(sourceUrl);
      } catch (error) {
        console.error('Failed to open URL:', error);
        window.open(sourceUrl, '_blank');
      }
    }, { once: false, passive: false });
  }

  return link;
}

function normalizeUrlCandidate(value) {
  let url = String(value || '').trim();
  if (!url) return url;

  // Recover URLs that came back as markdown or partially duplicated markdown:
  //   [label](https://example.test/doc.pdf)
  //   https://example.test/doc.pdf](https://example.test/doc.pdf
  const markdown = url.match(/^\[[^\]]+\]\((https?:\/\/[^\s)]+)\)?$/i);
  if (markdown) {
    url = markdown[1];
  } else {
    const duplicatedMarkdown = url.match(/^https?:\/\/[^\s\]]+\]\((https?:\/\/[^\s)]+)\)?$/i);
    if (duplicatedMarkdown) {
      url = duplicatedMarkdown[1];
    }
  }

  return stripTrailingUrlPunctuation(url);
}

function stripTrailingUrlPunctuation(url) {
  let cleaned = String(url || '').trim();
  while (/[.,;:*]+$/.test(cleaned)) {
    cleaned = cleaned.slice(0, -1);
  }

  while (cleaned.endsWith(')')) {
    const opens = (cleaned.match(/\(/g) || []).length;
    const closes = (cleaned.match(/\)/g) || []).length;
    if (closes <= opens) break;
    cleaned = cleaned.slice(0, -1);
  }

  return cleaned;
}

function appendTextWithBreaks(parent, text) {
  const parts = String(text || '').split(/\r?\n/);
  parts.forEach((part, index) => {
    if (index > 0) parent.appendChild(document.createElement('br'));
    if (part) parent.appendChild(document.createTextNode(part));
  });
}

function appendLinkedText(parent, text) {
  const input = String(text || '');
  const tokenPattern = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)?|https?:\/\/[^\s<>"']+/gi;
  let lastIndex = 0;
  let match;

  while ((match = tokenPattern.exec(input)) !== null) {
    if (match.index > lastIndex) {
      appendTextWithBreaks(parent, input.slice(lastIndex, match.index));
    }

    const label = match[1] || null;
    const rawUrl = match[2] || match[0];
    const bareUrlText = !match[2] && !rawUrl.includes('](') ? stripTrailingUrlPunctuation(rawUrl) : rawUrl;
    const href = normalizeUrlCandidate(bareUrlText);
    const suffix = bareUrlText.length < rawUrl.length ? rawUrl.slice(bareUrlText.length) : '';
    const link = createSourceLink(href);
    link.textContent = label || href;
    link.style.textDecoration = 'underline';
    parent.appendChild(link);
    if (suffix) {
      appendTextWithBreaks(parent, suffix);
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < input.length) {
    appendTextWithBreaks(parent, input.slice(lastIndex));
  }
}

function createFailureReasonNode(failureReason) {
  const container = document.createElement('div');
  container.className = 'failure-reason-text';
  appendLinkedText(container, failureReason);
  return container;
}

/**
 * Create failure reason container with clickable failed URLs
 * @param {Array<{url: string, reason: string}>} failedUrls
 * @returns {HTMLDivElement}
 */
function createFailureContainer(failedUrls) {
  const getDomain = (url) => {
    try { return new URL(url).hostname.replace('www.', ''); }
    catch { return url.length > 40 ? url.substring(0, 37) + '...' : url; }
  };

  const container = document.createElement('div');
  container.style.lineHeight = '1.5';
  container.appendChild(document.createTextNode(
    'Checked ' + failedUrls.length + ' candidate' + (failedUrls.length > 1 ? 's' : '') + ', none yielded viable text. Failed: '
  ));

  failedUrls.forEach(({ url, reason }, index) => {
    if (index > 0) container.appendChild(document.createTextNode('; '));

    const link = createSourceLink(url);
    link.textContent = getDomain(url);
    link.style.textDecoration = 'underline';
    link.style.color = '#0066cc';
    container.appendChild(link);

    const reasonSpan = document.createElement('span');
    reasonSpan.textContent = ` (${reason})`;
    reasonSpan.style.color = '#666';
    container.appendChild(reasonSpan);
  });

  return container;
}

/**
 * Create a collapsible "Details" toggle for technical fields
 * @param {Array<[string, string]>} pairs - Array of [label, value] pairs
 * @returns {HTMLDivElement}
 */
function createDetailsToggle(pairs, extraNodes = []) {
  const wrapper = document.createElement('div');
  wrapper.className = 'citation-details';

  const btn = document.createElement('button');
  btn.className = 'details-toggle';
  btn.setAttribute('aria-expanded', 'false');

  const chev = document.createElement('span');
  chev.className = 'chev';
  chev.textContent = '›';

  const lbl = document.createElement('span');
  lbl.textContent = 'Details';

  btn.append(chev, lbl);

  // Inline summary of values shown collapsed
  const summary = document.createElement('span');
  summary.className = 'details-summary';
  summary.textContent = pairs.map(([k, v]) => v).join(' · ');
  btn.append(summary);

  const body = document.createElement('div');
  body.className = 'details-body';
  const detailTable = elTable(pairs);
  body.append(detailTable);
  for (const node of extraNodes) {
    if (node) body.append(node);
  }

  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    const expanded = btn.getAttribute('aria-expanded') === 'true';
    btn.setAttribute('aria-expanded', String(!expanded));
    body.classList.toggle('show', !expanded);
  });

  wrapper.append(btn, body);
  return wrapper;
}

/**
 * Build the list of distinct atomic claims (with per-claim verdicts when available)
 * shown inside the collapsible Details section.
 * @param {string[]} claims - Atomic claim texts (from the parsed citation).
 * @param {Array<{proposition:string,label:string,calibration:number,rationale:string}>|null} verdicts - Per-claim verdicts.
 * @returns {HTMLDivElement|null}
 */
function createAtomicClaimsList(claims, verdicts) {
  const hasVerdicts = Array.isArray(verdicts) && verdicts.length > 0;
  const items = hasVerdicts
    ? verdicts.map((v) => ({
        text: v?.proposition || '',
        label: v?.label || null,
        calibration: v?.calibration ?? null,
        rationale: v?.rationale || null,
      }))
    : (Array.isArray(claims) ? claims.map((c) => ({ text: typeof c === 'string' ? c : String(c), label: null })) : []);
  if (items.length === 0) return null;

  const box = document.createElement('div');
  box.className = 'atomic-claims';

  const header = document.createElement('div');
  header.className = 'atomic-claims-header';
  header.textContent = `Distinct claims (${items.length})`;
  box.append(header);

  items.forEach((it, i) => {
    const row = document.createElement('div');
    row.className = 'atomic-claim-item';

    const num = document.createElement('span');
    num.className = 'atomic-claim-num';
    num.textContent = `${i + 1}.`;
    row.append(num);

    const txt = document.createElement('span');
    txt.className = 'atomic-claim-text';
    txt.textContent = it.text || '(empty)';
    row.append(txt);

    if (it.label) {
      const badge = document.createElement('span');
      badge.className = badgeClass(it.label);
      badge.textContent = it.label;
      row.append(badge);
    }

    if (it.calibration != null) {
      const calibration = document.createElement('span');
      calibration.className = 'atomic-claim-calibration';
      calibration.textContent = String(it.calibration);
      row.append(calibration);
    }

    if (it.rationale) {
      const rationale = document.createElement('div');
      rationale.className = 'atomic-claim-rationale';
      rationale.textContent = it.rationale;
      row.append(rationale);
    }
    box.append(row);
  });

  return box;
}

/**
 * Create snippets section if snippets are available
 * @param {Object} data - Citation data
 * @param {string} cid - Citation ID
 * @returns {HTMLDivElement|null}
 */
function createSnippetsSection(data, cid) {
  // Check for evidence_snips first (highest priority)
  const snipsList = Array.isArray(data?.evidence_snips) ? data.evidence_snips
    : Array.isArray(data?.snips) ? data.snips
    : Array.isArray(data?.analysis?.snips) ? data.analysis.snips
    // Reopened documents: the persisted evidence rides on the cached verdict
    : Array.isArray(data?.congruence_result?.evidence_snips) ? data.congruence_result.evidence_snips
    : null;
  
  // If we have multiple snippets, use those instead of single quote_check snippet
  if (Array.isArray(snipsList) && snipsList.length > 0) {
    console.log(`Creating snippets section for ${snipsList.length} snippets:`, snipsList);
    const snips = document.createElement('div');
    snips.className = 'snips';

    const sh = document.createElement('button');
    sh.className = 'snips-header';
    sh.setAttribute('aria-expanded', 'false');
    
    const chev = document.createElement('span');
    chev.className = 'chev';
    chev.textContent = '›';
    
    const lbl = document.createElement('span');
    lbl.textContent = `Show ${snipsList.length} snippet${snipsList.length > 1 ? 's' : ''}`;
    
    sh.onclick = () => {
      const body = snips.querySelector('.snips-body');
      const expanded = sh.getAttribute('aria-expanded') === 'true';
      sh.setAttribute('aria-expanded', String(!expanded));
      body.classList.toggle('show', !expanded);
    };
    sh.append(chev, lbl);

    const sb = document.createElement('div');
    sb.className = 'snips-body';
    snipsList.forEach((s) => {
      const sn = document.createElement('div');
      sn.className = 'snip';
      sn.textContent = typeof s === 'string' ? s : JSON.stringify(s, null, 2);
      sb.append(sn);
    });

    snips.append(sh, sb);
    console.log('Snippets section appended to block');
    return snips;
  }
  
  // Fallback: Single snippet from quote check
  const q = data?.quote_check || data?.quoteCheck || null;
  if (q && q.best_snip) {
    const snips = document.createElement('div');
    snips.className = 'snips';
    
    const sh = document.createElement('button');
    sh.className = 'snips-header';
    sh.setAttribute('aria-expanded', 'true');
    
    const chev = document.createElement('span');
    chev.className = 'chev';
    chev.textContent = '›';
    
    const lbl = document.createElement('span');
    lbl.textContent = 'Show 1 snippet';
    
    const sb = document.createElement('div');
    sb.className = 'snips-body show';
    
    const sn = document.createElement('div');
    sn.className = 'snip';
    sn.textContent = q.best_snip;
    sb.append(sn);
    
    sh.onclick = () => {
      const ex = sh.getAttribute('aria-expanded') === 'true';
      sh.setAttribute('aria-expanded', String(!ex));
      sb.classList.toggle('show', !ex);
    };
    
    sh.append(chev, lbl);
    snips.append(sh, sb);
    return snips;
  }

  return null;
}

/**
 * Render audit results for a footnote
 * @param {HTMLElement} appEl - App container element
 * @param {string|number} footnoteId - Footnote ID
 * @param {Object} resultObj - Results object
 * @param {Object} options - Rendering options
 */
export function renderAuditResults(appEl, footnoteId, resultObj, options = {}) {
  console.log('[DEBUG] renderAuditResults called with:', { footnoteId, resultObj });

  if (!appEl) {
    console.error('[DEBUG] renderAuditResults: appEl is undefined');
    return;
  }

  const card = appEl.querySelector(`.card[data-id="${CSS.escape(String(footnoteId))}"]`);
  console.log('[DEBUG] Found card:', card);
  if (!card) {
    console.log('[DEBUG] No card found, returning early');
    return;
  }
  
  const body = card.querySelector('.card-body');
  console.log('[DEBUG] Found card body:', body);
  
  const container = body.querySelector('.results');
  container.querySelectorAll('.joint-group-summary').forEach(node => node.remove());
  const jointResults = { ...(options.jointResults || {}) };
  for (const entry of Object.values(resultObj || {})) {
    const group = entry?.joint_group_result || entry?.congruence?.joint_group_result
      || entry?.congruence_result?.joint_group_result;
    if (group?.group_id && !jointResults[group.group_id]) jointResults[group.group_id] = group;
  }
  for (const group of Object.values(jointResults)) {
    const summary = document.createElement('section');
    const status = group.label === 'supports' ? 'supports'
      : ['partial', 'insufficient'].includes(group.label) ? 'partial'
      : ['error', 'pending'].includes(group.label) ? 'pending' : 'issue';
    summary.className = `joint-group-summary joint-group-summary--${status}`;
    const heading = document.createElement('strong');
    heading.textContent = 'Joint check';
    const verdict = document.createElement('span');
    verdict.className = `joint-group-verdict joint-group-verdict--${status}`;
    verdict.textContent = group.label === 'supports' ? 'Verified together'
      : group.label === 'partial' ? 'Partially verified'
      : group.label === 'insufficient' ? 'Evidence incomplete'
      : String(group.label || 'Pending').replaceAll('_', ' ');
    const explanation = document.createElement('p');
    explanation.textContent = group.rationale || '';
    const counts = document.createElement('small');
    const contributions = group.member_contributions || [];
    const verified = contributions.filter(member => member.contribution_label === 'contributes').length;
    const notEstablished = contributions.filter(member => member.contribution_label === 'does_not_contribute').length;
    const contradicted = contributions.filter(member => member.contribution_label === 'contradicts').length;
    const unresolved = group.unresolved_members?.length
      ?? contributions.filter(member => member.contribution_label === 'non-verifiable').length;
    const parts = [`${verified} contribution${verified === 1 ? '' : 's'} verified`];
    if (notEstablished) parts.push(`${notEstablished} not established`);
    if (contradicted) parts.push(`${contradicted} contradicting`);
    if (unresolved) parts.push(`${unresolved} unresolved`);
    counts.textContent = parts.join(' · ');
    summary.append(heading, verdict, explanation, counts);
    if (Array.isArray(group.atomic_claim_verdicts) && group.atomic_claim_verdicts.length) {
      const coverage = document.createElement('div');
      const supported = group.atomic_claim_verdicts.filter(v => v.label === 'supports').length;
      coverage.textContent = `Atomic claims: ${supported}/${group.atomic_claim_verdicts.length} supported`;
      summary.append(coverage);
    }
    if (Array.isArray(group.secondary_labels) && group.secondary_labels.length) {
      const warnings = document.createElement('div');
      warnings.textContent = `Warnings: ${group.secondary_labels.join(', ')}`;
      summary.append(warnings);
    }
    container.prepend(summary);
  }
  console.log('[DEBUG] Found results container:', container);
  
  // Check if we have actual results (not just placeholders)
  const normalizedEntries = normalizeAuditEntries(resultObj);
  const hasRealResults = normalizedEntries.length > 0 && 
    !normalizedEntries.every(([cid, data]) => 
      cid.startsWith('__') || 
      (data && typeof data === 'object' && data.status === 'pending')
    );
  
  // If we have real results, clear inline asks as the resolution is complete
  if (hasRealResults) {
    console.log('[DEBUG] Clearing inline asks as resolution is complete');
    const existingAsks = container.querySelectorAll('.fc-inline-ask');
    existingAsks.forEach(ask => ask.remove());
  } else {
    // Preserve any inline asks that might already be displayed (during processing)
    const existingAsks = Array.from(container.querySelectorAll('.fc-inline-ask'));
    console.log('[DEBUG] Existing asks to preserve:', existingAsks.length);
    
    //console.log('[DEBUG] Container innerHTML before processing:', container.innerHTML);

    const normalizedObject = Object.fromEntries(normalizedEntries);
    updateHeaderStatus(appEl, footnoteId, normalizedObject, jointResults);

    if (!normalizedEntries.length) {
      console.log('[DEBUG] No valid resultObj, setting "No results"');
      container.textContent = 'No results.';
      return;
    }

    console.log('[DEBUG] Processing resultObj entries:', normalizedEntries.map(([cid]) => cid));
    normalizedEntries.forEach(([rawCid, rawData]) => {
      const cid = rawCid;
      const data = (rawData && typeof rawData === 'object') ? rawData : { rationale: rawData };
      console.log(`[DEBUG] Processing citation ${cid}:`, data);

      // Find existing citation block to replace
      const existingBlock = container.querySelector(`.citation-block[data-cite-id="${cid}"]`);
      if (!existingBlock) {
        console.log(`[DEBUG] No existing block found for citation ${cid}, creating new one`);
        // If no existing block, create a new one (fallback)
        const newBlock = document.createElement('div');
        newBlock.className = 'citation-block';
        newBlock.dataset.citeId = cid;
        newBlock.style.marginTop = '10px';
        container.appendChild(newBlock);
        renderCitationBlock(newBlock, cid, data, options);
        return;
      }

      console.log(`[DEBUG] Replacing existing block for citation ${cid}`);
      // Replace the existing block content
      renderCitationBlock(existingBlock, cid, data, options);
    });

    clearFinalResultPendingMarkers(container);

    // Re-append any preserved asks
    existingAsks.forEach(n => container.appendChild(n));
    //console.log('[DEBUG] Container after processing:', container.innerHTML);
    return;
  }

  //console.log('[DEBUG] Container innerHTML before processing:', container.innerHTML);

  const normalizedObject = Object.fromEntries(normalizedEntries);

  updateHeaderStatus(appEl, footnoteId, normalizedObject, jointResults);

  if (!normalizedEntries.length) {
    console.log('[DEBUG] No valid resultObj, setting "No results"');
    container.textContent = 'No results.';
    return;
  }

  console.log('[DEBUG] Processing resultObj entries:', normalizedEntries.map(([cid]) => cid));
  normalizedEntries.forEach(([rawCid, rawData]) => {
    const cid = rawCid;
    const data = (rawData && typeof rawData === 'object') ? rawData : { rationale: rawData };
    console.log(`[DEBUG] Processing citation ${cid}:`, data);

    // Find existing citation block to replace
    const existingBlock = container.querySelector(`.citation-block[data-cite-id="${cid}"]`);
    if (!existingBlock) {
      console.log(`[DEBUG] No existing block found for citation ${cid}, creating new one`);
      // If no existing block, create a new one (fallback)
      const newBlock = document.createElement('div');
      newBlock.className = 'citation-block';
      newBlock.dataset.citeId = cid;
      newBlock.style.marginTop = '10px';
      container.appendChild(newBlock);
      renderCitationBlock(newBlock, cid, data, options);
      return;
    }

    console.log(`[DEBUG] Replacing existing block for citation ${cid}`);
    // Replace the existing block content
    renderCitationBlock(existingBlock, cid, data, options);
  });

  clearFinalResultPendingMarkers(container);

  //console.log('[DEBUG] Container after processing:', container.innerHTML);
}

/**
 * Alias for backward compatibility
 * @param {HTMLElement} appEl - App container element
 * @param {string|number} footnoteId - Footnote ID
 * @param {Object} resultObj - Results object
 * @param {Object} options - Rendering options
 */
export function RenderFinalResult(appEl, footnoteId, resultObj, options = {}) {
  return renderAuditResults(appEl, footnoteId, resultObj, options);
}
